import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ApiClient from '../../methods/api/apiClient';
import Layout from '../../components/global/layout';
import environment from '../../environment';
import loader from '../../methods/loader';
import { FaCalendarCheck, FaCheckCircle, FaStar, FaHourglassHalf } from 'react-icons/fa';

// "CM" subpage under Engagement Analytics — ConnectMeet scheduling + rating metrics.

const CmAnalytics = () => {
    const user = useSelector((state: any) => state.user);
    const history = useNavigate();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const connectMeetLabel = user?.connectMeetLabel || 'ConnectMeet';

    useEffect(() => {
        const roleId = user?.customerRole?._id || user?.customerRole;
        if (roleId && !environment.groupLeaderRoleIds?.includes(roleId)) history('/dashboard');
    }, [user]);

    useEffect(() => {
        const activeGroup = user?.groups?.find((g: any) => g.status === 'active');
        const groupId = user?.lastUsedGroupId?._id || user?.lastUsedGroupId
            || user?.groupId?._id || user?.groupId
            || activeGroup?.groupId?._id || activeGroup?.groupId;
        if (!groupId) return;
        loader(true);
        ApiClient.post('api/engagement/cm-analytics', { groupId }).then((res: any) => {
            loader(false); setLoading(false);
            if (res.success) setData(res.data);
        }).catch(() => { loader(false); setLoading(false); });
    }, [user]);

    if (loading) return <Layout><div className="p-6 text-gray-500">Loading {connectMeetLabel} data…</div></Layout>;
    if (!data || data.totalSessions === 0) return <Layout><div className="p-6 text-gray-500">No {connectMeetLabel} scheduling data yet for this group.</div></Layout>;

    const f = data.funnel || {};
    return (
        <Layout>
            <div className="p-6">
                <h2 className="text-2xl font-bold mb-6">{connectMeetLabel} Analytics</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Kpi icon={<FaCalendarCheck className="text-blue-600" />} tint="blue" label={`Total ${connectMeetLabel}s`} value={data.totalSessions} sub={`${f.Assigned || 0} assigned · ${f.Scheduled || 0} scheduled · ${f.Completed || 0} completed`} />
                    <Kpi icon={<FaCheckCircle className="text-green-600" />} tint="green" label="Submission Rate" value={`${data.submissionRate}%`} sub={`${data.submittedMembers} of ${data.totalMembers} members responded`} />
                    <Kpi icon={<FaStar className="text-yellow-500" />} tint="yellow" label="Avg Overall Value" value={data.avgOverall != null ? `${data.avgOverall} / 3` : '—'} sub={`${data.ratingsCount} ratings`} />
                    <Kpi icon={<FaHourglassHalf className="text-orange-500" />} tint="orange" label="Auto-scheduled" value={data.autoScheduled} sub={`${data.fullyMatched} fully matched · ${data.failed} no match`} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <Panel title="Meeting mode">
                        <Bar label="In-person" value={data.modeSplit?.['in-person'] || 0} total={data.totalSessions} />
                        <Bar label="Virtual" value={data.modeSplit?.virtual || 0} total={data.totalSessions} />
                    </Panel>
                    <Panel title="Activity preference">
                        {Object.entries(data.activityDist || {}).map(([k, v]: any) => (
                            <Bar key={k} label={k} value={v} total={data.submittedMembers || 1} />
                        ))}
                    </Panel>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <Panel title="Overall value distribution">
                        {['3', '2', '1'].map(n => (
                            <Bar key={n} label={`${n} ${n === '3' ? '(best)' : n === '1' ? '(worst)' : ''}`} value={data.overallDist?.[n] || 0} total={data.ratingsCount || 1} />
                        ))}
                    </Panel>
                    <Panel title="Most-valued participants (avg peer score)">
                        {data.peerScores?.length ? (
                            <div className="space-y-1 max-h-56 overflow-y-auto">
                                {data.peerScores.map((p: any, i: number) => (
                                    <div key={i} className="flex justify-between text-sm py-1">
                                        <span className="text-gray-700">{p.name}</span>
                                        <span className="text-gray-500">{p.avg} / 3 <span className="text-xs text-gray-400">({p.count})</span></span>
                                    </div>
                                ))}
                            </div>
                        ) : <p className="text-sm text-gray-400">No peer ratings yet.</p>}
                    </Panel>
                </div>
            </div>
        </Layout>
    );
};

const TINT: Record<string, string> = { blue: 'bg-blue-100', green: 'bg-green-100', yellow: 'bg-yellow-100', orange: 'bg-orange-100' };
const Kpi = ({ icon, tint, label, value, sub }: any) => (
    <div className="bg-white rounded-xl shadow p-5">
        <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-full ${TINT[tint] || 'bg-gray-100'} flex items-center justify-center`}>{icon}</div>
            <span className="text-sm text-gray-500">{label}</span>
        </div>
        <p className="text-3xl font-bold text-gray-800">{value}</p>
        <p className="text-xs text-gray-400 mt-1">{sub}</p>
    </div>
);

const Panel = ({ title, children }: any) => (
    <div className="bg-white rounded-xl shadow p-5">
        <h3 className="font-semibold mb-3">{title}</h3>
        {children}
    </div>
);

const Bar = ({ label, value, total }: { label: string; value: number; total: number }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div className="mb-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{label}</span><span>{value}</span></div>
            <div className="w-full bg-gray-100 rounded-full h-2"><div className="bg-orange-500 h-2 rounded-full" style={{ width: `${pct}%` }} /></div>
        </div>
    );
};

export default CmAnalytics;
